import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Plus, CheckCircle, AlertTriangle, Calendar, DollarSign } from 'lucide-react';

interface CampaignCreationPanelProps {
  packVersionId: number;
  onCampaignCreated?: (campaign: any) => void;
}

interface CampaignValidation {
  canCreate: boolean;
  blockers: Array<{
    type: string;
    message: string;
    severity: 'blocking' | 'warning';
  }>;
  warnings: Array<{
    type: string;
    message: string;
    severity: 'blocking' | 'warning';
  }>;
}

interface CampaignData {
  startDate: string;
  endDate: string;
  budget: number;
  priority: 'low' | 'normal' | 'high';
  notes: string;
}

export const CampaignCreationPanel: React.FC<CampaignCreationPanelProps> = ({ 
  packVersionId, 
  onCampaignCreated 
}) => {
  const [validation, setValidation] = useState<CampaignValidation | null>(null);
  const [campaignData, setCampaignData] = useState<CampaignData>({
    startDate: '',
    endDate: '',
    budget: 0,
    priority: 'normal',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [createdCampaign, setCreatedCampaign] = useState<any>(null);

  // Validate campaign creation on component mount
  useEffect(() => {
    validateCampaignCreation();
  }, [packVersionId]);

  const validateCampaignCreation = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/pack-versions/${packVersionId}/create-campaign/validate`, {
        credentials: 'omit'
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidation(data);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to validate campaign creation');
      }
    } catch (err) {
      setError('Network error while validating campaign creation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    // Validate form data
    if (!campaignData.startDate || !campaignData.endDate) {
      setError('Start date and end date are required');
      return;
    }

    if (campaignData.budget <= 0) {
      setError('Budget must be greater than 0');
      return;
    }

    if (new Date(campaignData.startDate) >= new Date(campaignData.endDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`/api/pack-versions/${packVersionId}/create-campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify(campaignData)
      });

      const result = await response.json();

      if (result.success) {
        setCreatedCampaign(result.campaign);
        setError(null);
        onCampaignCreated?.(result.campaign);
      } else {
        setError(result.error || 'Failed to create campaign');
      }
    } catch (err) {
      setError('Network error while creating campaign');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCampaignData({
      startDate: '',
      endDate: '',
      budget: 0,
      priority: 'normal',
      notes: ''
    });
    setShowForm(false);
    setCreatedCampaign(null);
    setError(null);
  };

  if (isLoading && !validation) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Validating campaign creation...</span>
        </CardContent>
      </Card>
    );
  }

  if (createdCampaign) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            Campaign Created Successfully!
          </CardTitle>
          <CardDescription>
            Your campaign has been created and is ready for execution
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="font-medium text-green-800">{createdCampaign.name}</div>
            <div className="text-sm text-green-600 mt-1">{createdCampaign.description}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Campaign ID</Label>
              <div className="text-sm text-muted-foreground">{createdCampaign.id}</div>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <Badge variant="default">{createdCampaign.status}</Badge>
            </div>
            <div>
              <Label className="text-sm font-medium">Store Count</Label>
              <div className="text-sm text-muted-foreground">{createdCampaign.storeCount}</div>
            </div>
            <div>
              <Label className="text-sm font-medium">Format Count</Label>
              <div className="text-sm text-muted-foreground">{createdCampaign.formatCount}</div>
            </div>
          </div>

          <Button onClick={resetForm} variant="outline" className="w-full">
            Create Another Campaign
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Create Campaign from Pack Version
        </CardTitle>
        <CardDescription>
          Create a new campaign using the locked pack version {packVersionId}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Validation Status */}
        {validation && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">Can Create Campaign:</span>
              <Badge variant={validation.canCreate ? "default" : "destructive"}>
                {validation.canCreate ? "Yes" : "No"}
              </Badge>
            </div>

            {/* Blockers */}
            {validation.blockers.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-destructive">Blockers:</span>
                <ul className="space-y-1">
                  {validation.blockers.map((blocker, index) => (
                    <li key={index} className="text-sm text-destructive flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3" />
                      {blocker.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {validation.warnings.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-yellow-600">Warnings:</span>
                <ul className="space-y-1">
                  {validation.warnings.map((warning, index) => (
                    <li key={index} className="text-sm text-yellow-600 flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3" />
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Campaign Creation Form */}
        {validation?.canCreate && (
          <div className="space-y-4">
            {!showForm ? (
              <Button 
                onClick={() => setShowForm(true)}
                className="w-full"
                disabled={isLoading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            ) : (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={campaignData.startDate}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, startDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date *</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={campaignData.endDate}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, endDate: e.target.value }))}
                      min={campaignData.startDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget">Budget ($) *</Label>
                    <Input
                      id="budget"
                      type="number"
                      min="0"
                      step="0.01"
                      value={campaignData.budget}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, budget: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <select
                      id="priority"
                      value={campaignData.priority}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={campaignData.notes}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about the campaign..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleCreateCampaign}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Campaign
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Success Message */}
        {validation?.canCreate && validation.blockers.length === 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              This pack version is ready for campaign creation. Fill out the form above to get started.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
